import { ScenarioOperation, ScenarioView } from "./ScenarioTypes";
import { FULL_FILL_POLICY } from "./ActivationPanel";

export const guideRequestID = (generation: number, step: number) =>
  `guided-v1-generation-${generation}-step-${step}`;

const baseline: Record<string, string> = {
  paid_fee_usd: "0",
  waived_fee_usd: "0",
  cash_accrued_usd: "0",
  cash_eligible_usd: "0",
  cash_held_usd: "0",
  dao_value_usd: "0",
  available_points: "0",
  pending_points: "0",
  allowance_remaining_usd: "0",
  simulated_cash_paid_usd: "0",
  total_notional: "0",
};
const firstTrade = {
  ...baseline,
  paid_fee_usd: "320",
  cash_accrued_usd: "74",
  cash_held_usd: "74",
  dao_value_usd: "80",
  pending_points: "220",
  total_notional: "1000000",
};
const vested = {
  ...firstTrade,
  cash_held_usd: "0",
  cash_eligible_usd: "74",
  available_points: "220",
  pending_points: "0",
};
const granted = { ...vested, available_points: "860" };
const funded = { ...vested, allowance_remaining_usd: "160" };
const secondTrade = {
  ...funded,
  paid_fee_usd: "480",
  waived_fee_usd: "160",
  cash_accrued_usd: "111",
  cash_held_usd: "37",
  dao_value_usd: "120",
  pending_points: "110",
  allowance_remaining_usd: "0",
  total_notional: "2000000",
};
const allVested = {
  ...secondTrade,
  cash_held_usd: "0",
  cash_eligible_usd: "111",
  available_points: "330",
  pending_points: "0",
};
const paid = {
  ...allVested,
  cash_eligible_usd: "0",
  simulated_cash_paid_usd: "111",
};
export const checkpoints = [
  baseline,
  firstTrade,
  vested,
  granted,
  funded,
  secondTrade,
  secondTrade,
  allVested,
  paid,
];

const fullyFunded = { ...vested, allowance_remaining_usd: "320" };
const fullyCovered = {
  ...vested,
  waived_fee_usd: "320",
  total_notional: "2000000",
};
export const fullFillCheckpoints = [
  baseline,
  firstTrade,
  vested,
  { ...vested, available_points: "1500" },
  fullyFunded,
  fullyCovered,
  fullyCovered,
  fullyCovered,
  { ...fullyCovered, cash_eligible_usd: "0", simulated_cash_paid_usd: "74" },
];

export const usesFullFillPolicy = (view: ScenarioView) =>
  view.dashboard.waiver_policy === FULL_FILL_POLICY;

export const metricLabels: Record<string, string> = {
  total_notional: "Total traded notional ($)",
  paid_fee_usd: "Fees paid ($)",
  waived_fee_usd: "Fees waived ($)",
  cash_accrued_usd: "Cash commission accrued ($)",
  cash_eligible_usd: "Cash eligible ($)",
  cash_held_usd: "Cash held ($)",
  dao_value_usd: "DAO value — unpaid ($)",
  available_points: "Available points",
  pending_points: "Pending points",
  allowance_remaining_usd: "Trader zero-fee budget ($)",
  simulated_cash_paid_usd: "Simulated cash paid ($)",
};

// Compare decimal strings exactly, without floating-point financial arithmetic.
export const sameAmount = (actual: string | undefined, expected: string) => {
  const normalize = (amount: string) =>
    amount
      .replace(/\.0*$/, "")
      .replace(/(\.\d*?)0+$/, "$1")
      .replace(/^0+(?=\d)/, "");
  return actual !== undefined && normalize(actual) === normalize(expected);
};

const steps = [
  [
    "Trade $1M BTC perpetual",
    "20% staking discount. This trade pays $320, earns $74 cash, $80 DAO value and 220 pending points.",
  ],
  [
    "Advance the scenario by 7 days",
    "$74 cash becomes eligible and 220 points become available.",
  ],
  [
    "Grant 640 welcome points",
    "The affiliate now has 860 available points, including the 220 earned points.",
  ],
  [
    "Fund a $160 zero-fee budget",
    "Spend 640 points at four points per $1 of fee coverage. Available points return to 220.",
  ],
  [
    "Repeat the $1M trade",
    "This trade pays $160 and waives $160. It earns $37 cash, $40 DAO value and 110 pending points. Totals: $111 cash and $120 DAO value. Partial-waiver economics are provisional.",
  ],
  [
    "Schedule a 5 bps taker fee",
    "Current perp taker stays at 4 bps; 5 bps starts after 7 days. The whole wallet is locked against further fee edits during that notice.",
  ],
  [
    "Advance another 7 days",
    "Perp taker becomes 5 bps. Cash eligible: $111. Available points: 330. Zero-fee budget: $0.",
  ],
  [
    "Simulate the cash payout",
    "$111 is marked simulated paid and eligible cash becomes $0. The $120 DAO value remains unpaid. No real funds move.",
  ],
];

const fullFillSteps = steps.map((step, index) => {
  const replacements: Record<number, string[]> = {
    2: [
      "Grant 1280 welcome points",
      "The affiliate now has 1500 available points, including the 220 earned points.",
    ],
    3: [
      "Fund a $320 zero-fee budget",
      "Spend 1280 points at four points per $1 of fee coverage. Available points return to 220.",
    ],
    4: [
      "Repeat the $1M trade",
      "The entire $320 fee is waived. This zero-fee volume earns no cash commission, DAO value, points or rewards. Totals remain $74 cash and $80 DAO value.",
    ],
    6: [
      "Advance another 7 days",
      "Perp taker becomes 5 bps. Cash eligible: $74. Available points: 220. Zero-fee budget: $0.",
    ],
    7: [
      "Simulate the cash payout",
      "$74 is marked simulated paid and eligible cash becomes $0. The $80 DAO value remains unpaid. No real funds move.",
    ],
  };
  return replacements[index] || step;
});

export const guideCompleted = (view: ScenarioView) => {
  const next = steps.findIndex(
    (_, index) =>
      !view.applied_request_ids?.includes(
        guideRequestID(view.generation, index + 1)
      )
  );
  return next === -1 ? steps.length : next;
};

export function guideCommand(
  view: ScenarioView,
  step: number
): { operation: ScenarioOperation; payload: Record<string, unknown> } {
  if (step === 1 || step === 5) {
    return {
      operation: "trade",
      payload: {
        wallet: view.fixtures.trader,
        instrument_id: "BTC-PERP",
        notional: "1000000",
        premium: "0",
        fills: 1,
        side: "taker",
        staking_discount: "0.2",
      },
    };
  }
  if (step === 2 || step === 7)
    return { operation: "advance_clock", payload: { seconds: 604800 } };
  if (step === 8) return { operation: "simulate_payout", payload: {} };
  const operations: Record<number, Record<string, unknown>> = {
    3: {
      operation: "grant_welcome",
      payload: {
        affiliate: view.fixtures.affiliate,
        points: usesFullFillPolicy(view) ? "1280" : "640",
      },
    },
    4: {
      operation: "allocate_allowance",
      payload: {
        wallet: view.fixtures.trader,
        dollars: usesFullFillPolicy(view) ? "320" : "160",
      },
    },
    6: {
      operation: "set_rates",
      payload: {
        wallet: view.fixtures.trader,
        rates: {
          perp_taker_bps: "5",
          perp_maker_bps: "2",
          option_taker_bps: "4.5",
          option_maker_bps: "2.5",
        },
      },
    },
  };
  return { operation: "program", payload: operations[step] };
}

export function ScenarioGuide({
  view,
  disabled,
  run,
}: {
  view: ScenarioView;
  disabled: boolean;
  run: (
    operation: ScenarioOperation,
    payload: Record<string, unknown>,
    requestID?: string
  ) => void;
}) {
  const completed = guideCompleted(view);
  const fullFillPolicy = usesFullFillPolicy(view);
  const expected = (fullFillPolicy ? fullFillCheckpoints : checkpoints)[
    completed
  ];
  const activeSteps = fullFillPolicy ? fullFillSteps : steps;
  const matches = Object.entries(expected).every(([key, value]) =>
    sameAmount(view.summary[key], value)
  );
  const ratesMatch =
    view.dashboard.affiliate.tier === "VIP3" &&
    sameAmount(view.account.perp_taker.rate_bps, completed >= 7 ? "5" : "4") &&
    sameAmount(view.account.rates.perp_maker_bps, "2") &&
    sameAmount(view.account.rates.option_taker_bps, "4.5") &&
    sameAmount(view.account.rates.option_maker_bps, "2.5") &&
    (completed === 6
      ? sameAmount(view.account.perp_taker.next_rate_bps, "5") &&
        Boolean(view.account.edit_locked_until)
      : !view.account.perp_taker.next_rate_bps);
  return (
    <section aria-label="Guided affiliate acceptance scenario">
      <h2>Tim, follow these steps to test the affiliate flow</h2>
      <p>
        Start with the default VIP3 affiliate, linked trader and DEMO code. Fees
        are 4/2 bps for perps and 4.5/2.5 bps for options. The hypothetical AEVO
        price is $0.25. Each button runs the backend program and checks its
        returned state.
      </p>
      <p>
        {fullFillPolicy
          ? "Confirmed policy: each fill pays its full tier fee or zero. Insufficient budget stays available for a smaller fill."
          : "Legacy scenario: this guide preserves the earlier proportional-waiver calculations. Create or reset a scenario to test the confirmed full-fill policy."}
      </p>
      <p>
        <strong>
          {completed} of {steps.length} steps applied.
        </strong>{" "}
        Use a fresh scenario for this guide. Advanced changes can change the
        expected results.
      </p>
      <ol>
        {activeSteps.map(([title, description], index) => (
          <li key={title} style={{ marginBottom: 18 }}>
            <strong>{title}</strong>
            {index < completed ? " — applied" : ""}
            <p style={{ margin: "6px 0" }}>{description}</p>
            {index === completed && (
              <button
                type="button"
                disabled={disabled || !matches || !ratesMatch}
                onClick={() => {
                  const next = guideCommand(view, index + 1);
                  run(
                    next.operation,
                    next.payload,
                    guideRequestID(view.generation, index + 1)
                  );
                }}
              >
                Run step {index + 1}: {title}
              </button>
            )}
          </li>
        ))}
      </ol>
      <h3>
        Current checkpoint:{" "}
        {completed ? `after step ${completed}` : "ready to begin"}
      </h3>
      {(!matches || !ratesMatch) && (
        <p role="alert">
          Current state differs from the guided checkpoint. Review the actual
          values below. Create a fresh scenario or reset this one before
          restarting the guide.
        </p>
      )}
      {matches && ratesMatch && (
        <p role="status">
          Checkpoint matches the expected backend values.
          {completed === steps.length
            ? " Guided simulation complete. Real trading, authentication and payout integration still need their separate testnet checks."
            : ""}
        </p>
      )}
      <div style={{ overflowX: "auto" }}>
        <table>
          <thead>
            <tr>
              <th>Measure</th>
              <th>Expected now</th>
              <th>Actual from backend</th>
              <th>Check</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(expected).map(([key, value]) => (
              <tr key={key}>
                <th>{metricLabels[key]}</th>
                <td>{value}</td>
                <td>{view.summary[key] ?? "Unavailable"}</td>
                <td>
                  {sameAmount(view.summary[key], value) ? "Pass" : "Mismatch"}
                </td>
              </tr>
            ))}
            <tr>
              <th>Perp taker rate (bps)</th>
              <td>
                {completed >= 7 ? "5" : "4"}
                {completed === 6 ? " → 5 after 7 days" : ""}
              </td>
              <td>
                {view.account.perp_taker.rate_bps}
                {view.account.perp_taker.next_rate_bps
                  ? ` → ${view.account.perp_taker.next_rate_bps} at ${view.account.perp_taker.next_effective_at}`
                  : ""}
              </td>
              <td>{ratesMatch ? "Pass" : "Mismatch"}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {view.account.edit_locked_until && (
        <p>
          Whole-wallet fee edit lock ends: {view.account.edit_locked_until}.
        </p>
      )}
    </section>
  );
}
